import React, { useContext, useEffect, useRef } from "react";
import { useSelector } from "react-redux";
import { Box } from "@mui/material";
import mapboxgl from "mapbox-gl";

import { makeVersionVectorTileURL, orderLayersByConfiguration } from "fond/api";
import { BASE_MAP_CUSTOM_OVERLAY_MARKER, BASE_MAP_IMAGE_OVERLAY_MARKER } from "fond/constants";
import SourceAndLayers from "fond/map/SourceAndLayers";
import { getCurrentProject } from "fond/project";
import { GroupConfig, Store } from "fond/types";
import { ImageOverlayItem } from "fond/types/overlays";
import { LayerStyle } from "fond/types/ProjectLayerConfig";
import { isVisible } from "fond/utils/configurations";

import { useOverlaysWithConfigs } from "./Overlays/useOverlaysWithConfigs";
import { MapContext } from "./MapProvider";
import { useStyleEpoch } from "./useStyleEpoch";

interface IProps {
  layerView: {
    [key: string]: boolean;
  };
}

/**
 * Renders a single image overlay on the map as a Mapbox image source + raster layer.
 */
const ImageOverlayLayer: React.FC<{ image: ImageOverlayItem; visible: boolean; beforeId: string }> = ({ image, visible, beforeId }) => {
  const { map } = useContext(MapContext);
  const addedRef = useRef(false);
  const styleEpoch = useStyleEpoch(map);

  const { ID, ImageUrl, ImageCorners, Opacity } = image;
  const sourceId = `image-overlay-source-${image.ID}`;
  const layerId = `image-overlay-layer-${image.ID}`;

  // Keep the latest pre-signed URL in a ref so polling refreshes don't
  // trigger the effect (which would cause the image to flash). The ref
  // is read when the effect actually needs to run (style change, corner update, etc.).
  const imageUrlRef = useRef(ImageUrl);
  imageUrlRef.current = ImageUrl;

  // Add or update the image source and layer
  useEffect(() => {
    if (!map) return;

    const source = map.getSource(sourceId) as mapboxgl.ImageSource | undefined;
    if (source) {
      source.updateImage({ url: imageUrlRef.current, coordinates: ImageCorners });
    } else {
      // Fall back to the overlay marker if the target beforeId layer
      // hasn't been created yet (e.g. the previous image failed to load).
      const insertBefore = map.getLayer(beforeId) ? beforeId : BASE_MAP_IMAGE_OVERLAY_MARKER;
      map.addSource(sourceId, {
        type: "image",
        url: imageUrlRef.current,
        coordinates: ImageCorners,
      });
      map.addLayer(
        {
          id: layerId,
          type: "raster",
          source: sourceId,
          paint: {
            "raster-opacity": Opacity,
          },
        },
        insertBefore
      );
      addedRef.current = true;
    }
  }, [map, sourceId, layerId, ImageCorners, Opacity, beforeId, styleEpoch]);

  // Update visibility
  useEffect(() => {
    if (map?.getLayer(layerId)) {
      map.setLayoutProperty(layerId, "visibility", visible ? "visible" : "none");
    }
  }, [map, layerId, visible, styleEpoch]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (!map) return;
      if (map.getLayer(layerId)) map.removeLayer(layerId);
      if (map.getSource(sourceId)) map.removeSource(sourceId);
    };
  }, [map, sourceId, layerId]);

  return null;
};

/**
 * Component responsible for rendering custom overlay layers on the map.
 * This component:
 * 1. Loads all custom overlays for the current version/project
 * 2. For each overlay, fetches its source version's configuration
 * 3. Renders each overlay's layers as vector tiles on the map
 * 4. Renders image overlays as Mapbox image sources
 *
 * This is separate from MapContent to keep overlay rendering independent from
 * the main project layers, allowing overlays to be displayed without affecting
 * the Legend.
 */
const OverlayMapContent: React.FC<IProps> = ({ layerView }: IProps) => {
  const versionId = useSelector((state: Store) => state.project.versionId);
  const project = useSelector((state: Store) => getCurrentProject(state.project));

  const { overlays: mergedConfig, overlayMetadata, imageOverlays, isLoading } = useOverlaysWithConfigs(versionId, project);

  const checkVisibility = (id: string, view: { [layerId: string]: boolean }) => {
    if (!mergedConfig) return false;
    return isVisible(mergedConfig, { id, layerView: view });
  };

  // Don't render anything if overlays are still loading or if there are no overlays
  if (isLoading || !mergedConfig) {
    return null;
  }

  // Extract styles from the merged overlay configuration
  const overlayStyles: LayerStyle[] = Object.values(mergedConfig.Data.entities).filter((entity): entity is LayerStyle => entity?.Type === "STYLE");

  // First get the parent "Overlays" group, then extract its children (the actual overlay groups)
  const rootGroup = mergedConfig.MapChildren.map((groupId: string) => mergedConfig.Data.entities[groupId]).find(
    (group): group is GroupConfig => group?.Type === "GROUP"
  );

  // The rootGroup contains a group for each overlay, which we need to extract and render.
  // Filter out the images-parent-group since those are rendered separately as image layers.
  const overlayGroups = rootGroup?.Children
    ? rootGroup.Children.map((childId: string) => mergedConfig.Data.entities[childId]).filter(
        (group): group is GroupConfig => group?.Type === "GROUP" && group.ID !== "images-parent-group"
      )
    : [];

  return (
    <Box data-testid="overlay-map-content">
      {/* Image overlays are rendered first so they stack below vector overlays.
          Each image inserts before the previous image's layer, chaining them
          downward from the overlay-marker. This keeps new images at the bottom
          and preserves the order on style reloads. */}
      {imageOverlays.map((image, index) => {
        const entityId = `image-overlay-${image.ID}`;
        const imageVisible = checkVisibility(entityId, layerView);
        const beforeId = index === 0 ? BASE_MAP_IMAGE_OVERLAY_MARKER : `image-overlay-layer-${imageOverlays[index - 1].ID}`;
        return <ImageOverlayLayer key={entityId} image={image} visible={imageVisible} beforeId={beforeId} />;
      })}
      {[...overlayGroups].reverse().map((group) => {
        if (!group || group.Type !== "GROUP") return null;

        // Get the overlay metadata for this group
        const metadata = overlayMetadata[group.ID];
        if (!metadata) {
          console.warn(`No metadata found for overlay group ${group.ID}`);
          return null;
        }

        const { overlayId, sourceVersionId } = metadata;

        // Get all layers that belong to this overlay group in the correct rendering order
        // orderLayersByConfiguration will recursively traverse nested groups and return
        // all layers and sublayers in the proper order for Mapbox rendering
        const groupLayers = orderLayersByConfiguration({
          ID: group.ID,
          Key: group.Key ?? "",
          SourceID: mergedConfig.SourceID || "",
          Type: "MapLayerConfig" as const,
          Data: mergedConfig.Data,
          MapChildren: group.Children || [],
        });

        if (groupLayers.length === 0) return null;

        // Create a unique source ID for this overlay group
        const sourceId = `overlay-${overlayId}`;

        return (
          <SourceAndLayers
            key={sourceId}
            sourceId={sourceId}
            source={{
              type: "vector",
              tiles: [makeVersionVectorTileURL(sourceVersionId)],
              promoteId: "id",
              maxzoom: 18,
            }}
            layers={groupLayers}
            styles={overlayStyles}
            layerVisibilities={layerView}
            isVisible={checkVisibility}
            versionId={sourceVersionId}
            // Overlays are actually Underlays in the sense that they are
            // drawn on top of the base map but below the main project layers
            beforeId={BASE_MAP_CUSTOM_OVERLAY_MARKER}
          />
        );
      })}
    </Box>
  );
};

export default OverlayMapContent;
